// Phase 7a — AC#4: surface the model_label tier as a coloured badge.
//
// The actual backend field is `model_label` at the top level of the
// airframe + threat documents (set by services/sim_v6_threat_seed.py
// when the v6 demo pack imports). Values are lower-case strings.
//
//   "validated"    → green badge, label "Validated"
//   "provisional"  → amber badge, label "Provisional"
//   "experimental" → red   badge, label "Experimental"
//
// Per AC#4 scope: render nothing when the field is missing or has an
// unrecognised value. No placeholder. No invented field.
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TIER_DEFS = {
    validated: {
        label: "Validated",
        cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
        tooltip:
            "Validated: model parameters cross-checked against flight-test or wind-tunnel evidence.",
    },
    provisional: {
        label: "Provisional",
        cls: "bg-amber-50 text-amber-800 border-amber-200",
        tooltip:
            "Provisional: parameters from compatibility manifest / engineering surrogates. Use for relative comparison; not flight-validated.",
    },
    experimental: {
        label: "Experimental",
        cls: "bg-rose-50 text-rose-700 border-rose-200",
        tooltip:
            "Experimental: prototype parameter set, evidence not yet attached. Treat results as exploratory only.",
    },
};

export const CREDIBILITY_FIELD = "model_label";

/**
 * @param {object} props
 * @param {object|null} [props.entity] – row dict; we read entity.model_label
 * @param {string|null} [props.tier]  – explicit override; bypasses entity
 * @param {string} [props.size]       – "xs" (default) | "sm"
 * @param {string} [props.testId]
 * @param {string} [props.className]  – appended after the size + tone classes
 */
export default function CredibilityBadge({
    entity = null,
    tier = null,
    size = "xs",
    testId,
    className = "",
}) {
    const raw = (tier ?? entity?.[CREDIBILITY_FIELD] ?? "")
        .toString()
        .trim()
        .toLowerCase();
    const def = TIER_DEFS[raw];
    if (!def) return null;

    const sizeCls =
        size === "sm"
            ? "text-[11px] px-2 py-0.5"
            : "text-[10px] px-1.5 py-0.5";

    const dataTier = raw;
    const a11y = `Credibility tier: ${def.label}`;

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        className={[
                            "inline-flex items-center font-mono uppercase tracking-wider rounded-md border",
                            sizeCls,
                            def.cls,
                            className,
                        ].join(" ")}
                        data-testid={testId || "credibility-badge"}
                        data-tier={dataTier}
                        aria-label={a11y}
                    >
                        {def.label}
                    </span>
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    className="max-w-xs text-[11px] leading-snug"
                >
                    {def.tooltip}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export { TIER_DEFS as CREDIBILITY_TIER_DEFS };
